import React, { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from 'context/AuthContext';
import { StoreAdminProvider } from 'context/StoreAdminContext';
import { CartProvider } from 'context/CartContext';
import { FavoritesProvider } from 'context/FavoritesContext';
import { CatalogPreferencesProvider } from 'context/CatalogPreferencesContext';
import { CatalogPresentationProvider } from 'context/CatalogPresentationContext';
import { StorefrontProvider, useStorefrontContext } from 'context/StorefrontContext';
import { TenantProvider } from 'context/TenantContext';
import { OfflineProvider } from 'context/OfflineContext';
import { createStoreTheme } from 'theme';
import AppRouter from 'routes/AppRouter';
import ErrorBoundary from 'components/feedback/ErrorBoundary';
import { resolveSiteName } from 'utils/themeDisplay';
import { buildGoogleFontsStylesheetUrl, getGoogleFontFamiliesToLoad } from 'constants/storeFonts';

const ThemedApp: React.FC = () => {
  const { theme, colorMode, loading } = useStorefrontContext();
  const muiTheme = useMemo(() => createStoreTheme(colorMode, theme), [colorMode, theme]);

  const siteName = theme ? resolveSiteName(theme) : '';
  const googleFontsUrl = buildGoogleFontsStylesheetUrl(getGoogleFontFamiliesToLoad(theme?.fontFamily));
  const waitForTheme = loading && !theme;
  const primaryColor = theme?.primaryColor?.trim();

  useEffect(() => {
    if (!primaryColor) return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = primaryColor;
  }, [primaryColor]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Helmet>
        {siteName && <title>{siteName}</title>}
        {siteName && <meta name="application-name" content={siteName} />}
        {googleFontsUrl && <link rel="preconnect" href="https://fonts.googleapis.com" />}
        {googleFontsUrl && <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />}
        {googleFontsUrl && <link rel="stylesheet" href={googleFontsUrl} />}
      </Helmet>
      <CssBaseline />
      <AuthProvider>
        <StoreAdminProvider>
          <CartProvider>
            <FavoritesProvider>
            <CatalogPreferencesProvider>
              <CatalogPresentationProvider>
                {!waitForTheme && (
                  <BrowserRouter>
                    <AppRouter />
                  </BrowserRouter>
                )}
              </CatalogPresentationProvider>
            </CatalogPreferencesProvider>
            </FavoritesProvider>
          </CartProvider>
        </StoreAdminProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <SnackbarProvider maxSnack={3} autoHideDuration={4000}>
      <ErrorBoundary variant="global">
        <TenantProvider>
          <OfflineProvider>
            <StorefrontProvider>
              <ThemedApp />
            </StorefrontProvider>
          </OfflineProvider>
        </TenantProvider>
      </ErrorBoundary>
    </SnackbarProvider>
  </HelmetProvider>
);

export default App;
